import {ComponentType, ReactNode} from 'react';
import {
  BaseUser,
  BodySite,
  BodySiteId,
  Condition,
  ConditionId,
  ScoringSystemItem,
  ScoringSystem
} from '@legit.health/ui';
import {UseFormReturn} from 'react-hook-form';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {useEventDispatcher} from '@/shared/contexts/EventDispatcherContext';
import {createDiagnosticReportCreatedEvent} from '@/shared/domainEvents/diagnosticReportCreatedEvent';
import useAnamnesisQuestions from '@/shared/hooks/useAnamnesisQuestions';
import DiagnosticReportForm from './DiagnosticReportForm';
import useConditionsInfo from './hooks/useConditionsInfo';
import {CreateDiagnosticReportResponse} from './types';
import anamnesisSessionStorage from './utils/anamnesisSessionStorage';

export default function DiagnosticReportFormContainer({
  onSuccess,
  patient,
  extraPostData,
  conditionId,
  bodySiteId,
  visitIdentifierComponent,
  noScoringSystems,
  renderItemLabel,
  withTemporaryImages = false,
  conditions
}: {
  bodySiteId?: BodySiteId | null;
  conditionId?: ConditionId | null;
  extraPostData?: (conditionId: string | null) => Record<string, any>;
  finalStepComponent?: ComponentType<{
    form: UseFormReturn<any>;
  }>;
  visitIdentifierComponent?: ComponentType<{
    onChange: (value: string) => void;
    value: string;
  }>;
  noScoringSystems: ReactNode;
  onSuccess: (response: CreateDiagnosticReportResponse) => void;
  patient: BaseUser;
  renderItemLabel?: (options: {
    item: ScoringSystemItem;
    scoringSystem: ScoringSystem;
    bodySite: BodySite;
  }) => string;
  withTemporaryImages?: boolean;
  conditions: Condition[];
}) {
  const {bodySites} = useSharedDataContext();
  const eventDispatcher = useEventDispatcher();
  const {
    data: {data: conditionsInfo}
  } = useConditionsInfo(patient.id);
  const {
    data: {data: anamnesisQuestions}
  } = useAnamnesisQuestions();

  function handleSuccess(response: CreateDiagnosticReportResponse) {
    anamnesisSessionStorage.remove();
    eventDispatcher.dispatch(
      createDiagnosticReportCreatedEvent({
        patientId: patient.id
      })
    );
    onSuccess(response);
  }

  return (
    <DiagnosticReportForm
      extraPostData={extraPostData}
      conditionId={conditionId}
      bodySiteId={bodySiteId}
      patient={patient}
      bodySites={bodySites}
      conditionsInfo={conditionsInfo}
      anamnesisQuestions={anamnesisQuestions}
      visitIdentifierComponent={visitIdentifierComponent}
      noScoringSystems={noScoringSystems}
      renderItemLabel={renderItemLabel}
      onSuccess={handleSuccess}
      withTemporaryImages={withTemporaryImages}
      conditions={conditions}
    />
  );
}
